import { Component, Input, OnInit } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { NavigationService } from 'src/app/navigation/navigation.service';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { ModuleManager } from 'src/shared/models/module-manager';
import { MetricSelectorService } from 'src/app/reusables/metric-selector/metric-selector.service';
import { MetricSelectorComponent } from 'src/app/reusables/metric-selector/metric-selector.component';
import { ModuleSettingsView } from 'src/shared/models/module-manager/module-manager-settings.models';
import { AgentStatesColorModalComponent } from '../../modals/agent-states-color-modal/agent-states-color-modal.component';
import { EState, SettingsDistinctStates, StateSetting } from '../../modules/current-agent-states/current-agent-states.models';
import { CurrentAgentStatesService } from '../../modules/current-agent-states/current-agent-states.service';

@Component({
    selector: 'cv-current-agent-states-settings',
    templateUrl: './current-agent-states-settings.component.html',
    styleUrls: ['./current-agent-states-settings.component.scss']
})
export class CurrentAgentStatesSettingsComponent implements OnInit {

    @Input() moduleManager: ModuleManager;

    version = 1; // This value is used to mark settings for migration
    moduleKey: string = null;
    _lastLoaded = null; // Marks when the data was last loaded.  Used by onSettingsOpened to avoid reloading if it's been less than a minute.
    _stateToColorMap: any;// Custom States custom colors mappings.  Key is the StateDisplayName of the color, value is the hex color code.
    MAX_RECENT_COLORS = 12; // How many custom colors to remember for the popup.

    cleaveSettings = {
        time: true,
        timePattern: ['h', 'm', 's'],
    };
    cachedResponse: SettingsDistinctStates = null;
    isFirstOpen = true;
    _modalOpened = false;
    dataLoading = false;
    openSettings = false;
    settings = {
        all: [], // all states, persisted by moduleManager.addSetting
        filteredLeft: [],
        filteredRight: [],
        selectedMetrics: [], // linked to metric selector
        useMetrics: false,
    };
    enabledSettings = 0;
    showAssignedSkills = false;
    showSourceRadio = false;
    includeLoggedIn = true;
    includeLoggedOut = true;
    statesInDisplayOrder: StateSetting[] = []; // Used in the slideout, since all states are in one list.
    colors = { /* Colors given by Design. Consult them before changing. */
        green: "#3D9649",
        purple: "#7C52BF",
        yellow: "#DC9C24",
        red: "#BD2026",
        blue: "#0078AC",
        orange: "#F24813",
        cyan: "#00BBBD",
        brown: "#A47748",
    };

    localizations = null;
    colorModalTitle = '';
    statesByEState = { // Used for faster lookup of ctrl.settings.all
        0: [], // LoggedOut
        1: [], // Available
        2: [], // Inbound
        4: [], // Outbound
        8: [], // Unavailable
        16: [], // Custom
    };
    stateNameFilter ='';
    showLoggedInAgentsText = "";
    showLoggedOutAgentsText = "";
    metricSelectorPlacement = 'att'; //placing the metric selector section after the placement attribute section

    slideout = ModuleSettingsView.Slideout;
    modal = ModuleSettingsView.Modal;
    cxOne = ModuleSettingsView.CXOneModal;

    constructor(
        public service: CurrentAgentStatesService,
        public navigationService: NavigationService,
        private ngbModalService: NgbModal,
        private localizationService: CVLocalizationService,
        private metricSelectorService: MetricSelectorService,
    ) { }

    get tooltipContexMetricstData() {
        return this.localizationService
            .getCachedText("Current_Agent_States_Module", "Tooltip_Metrics_Data",
                "This will allow you to display metrics data on the widget");
    }

    get stateKey() {
        const stateKey = this.moduleManager?.getModuleKey();
        if (stateKey == null)
            throw Error("stateKey could not be determined. Neither moduleManager is set.");
        return stateKey;
    }

    async ngOnInit() {

        this.moduleKey = this.moduleManager.getModuleKey();

        this.localizations = await this.localizationService.GetSection("Current_Agent_States_Module");
        this.colorModalTitle = this.localizations.get("Color_Modal_Title");

        this.showLoggedInAgentsText = this.localizationService.getCachedText("Current_Agent_States_Module",
            "Show_Logged_In", "Show logged in agents");
        this.showLoggedOutAgentsText = this.localizationService.getCachedText("Current_Agent_States_Module",
            "Show_Logged_Out", "Show logged out agents");

        this.moduleManager.addSetting({
            name: "StateSettings",
            getValue: () => {
                // Convert rawMin/rawMax to their int-value seconds:
                const placeValues = [3600, 60, 1]; // Seconds in an hour, Seconds in a minute, seconds in a second (to not need to check for special "is this a seconds value??" cases).
                this.settings.all.forEach(state => {
                    const minSplit = (state.rawMin != null && state.rawMin !== '') ? state.rawMin.split(':') : [];
                    const maxSplit = (state.rawMax != null && state.rawMax !== '') ? state.rawMax.split(':') : [];

                    if (minSplit.length > 0)
                        state.min = 0;
                    else if (minSplit.length === 0)
                        state.min = null; // When rawMin was empty, this value wasn't being unset. The endpoint uses this value always.

                    if (maxSplit.length > 0)
                        state.max = 0;
                    else if (maxSplit.length === 0)
                        state.max = null; // When rawMax was empty, this value wasn't being unset. The endpoint uses this value always.

                    for (let i = 0; i < minSplit.length; i++) {
                        if (minSplit[i] === '')
                            continue;
                        state.min += minSplit[i] * placeValues[i];
                    }

                    for (let j = 0; j < maxSplit.length; j++) {
                        if (maxSplit[j] === '')
                            continue;
                        state.max += maxSplit[j] * placeValues[j];
                    }
                });

                return this.settings;
            },
            onSettingLoaded: value => {
                let restoredStates;
                if (typeof value === "string" && value.length > 0) {
                    restoredStates = JSON.parse(value);
                } else if (typeof value === "object" && Array.isArray(value.all)) {
                    restoredStates = value;
                }

                if (typeof restoredStates !== "object" || !restoredStates.hasOwnProperty("all") || !Array.isArray(restoredStates.all))
                    restoredStates = { all: [] };

                restoredStates = this.filterSettings(restoredStates.all);
                this.categorizeAllStates(restoredStates);
            },
            defaultValue: {
                all: [],
            },
        });

        this.moduleManager.addSetting({
            name: "ShowAssignedSkills",
            getValue: () => !!this.showAssignedSkills,
            onSettingLoaded: value => this.showAssignedSkills = !!value,
            defaultValue: false,
        });

        this.moduleManager.addSetting({
            name: "CustomStateColors",
            getValue: () => this._stateToColorMap,
            onSettingLoaded: value => {
                if (typeof value === "object")
                    this._stateToColorMap = value;
                else
                    this._stateToColorMap = {};
            },
            defaultValue: {},
        });

        this.moduleManager.addSetting({
            name: "IncludeLoggedIn",
            getValue: () => this.includeLoggedIn,
            onSettingLoaded: value => this.includeLoggedIn = !!value,
            defaultValue: true,
        });

        this.moduleManager.addSetting({
            name: "IncludeLoggedOut",
            getValue: () => this.includeLoggedOut,
            onSettingLoaded: value => this.includeLoggedOut = !!value,
            defaultValue: true,
        });

        this.moduleManager.addSetting({
            name: "SelectedMetrics", // linked to metric selector
            getValue: () => this.settings.selectedMetrics,
            onSettingLoaded: (value) => {
                this.settings.selectedMetrics = value;
            },
            defaultValue: this.settings.selectedMetrics,
        });

        this.moduleManager.addSetting({
            name: "UseMetrics", // linked to metric selector
            getValue: () => this.settings.useMetrics,
            onSettingLoaded: (value) => {
                this.settings.useMetrics = value;
            },
            defaultValue: this.settings.useMetrics,
        });

        this.moduleManager.registerEvents({
            onSettingsOpened: () => {
                // Only load data if it's been longer than a minute:
                const settingsOpened = new Date().getTime();
                if (this.isFirstOpen || !this._lastLoaded ||  settingsOpened - this._lastLoaded > 1000 * 60)
                    this.loadData();
                else { // this is so current agent states settings gets populated if they open it again within one minute of opening it
                    this.setupStates(this.cachedResponse);
                }
            },
        });

        this.moduleManager.customSettingsAdded = true;
    }

    openMetricSelector() {
        const currentSelectedMetrics = this.moduleManager.customSettings.SelectedMetrics.getValue();
        const metricSelector =
        this.metricSelectorService.openModal(MetricSelectorComponent, this.moduleManager, currentSelectedMetrics, true);
        const newSelectedMetrics = [];
        metricSelector.result.then((metricSelectorResult) => {
            if(metricSelectorResult?.renameOnly) {
                this.settings.selectedMetrics.forEach((metric)=>{
                    const matchMetric = metricSelectorResult?.renamedMetrics.find(m => m.MetricID == metric.Id);
                    if(matchMetric)
                        metric.CustomName = matchMetric.MetricCustomName;
                })
            }
            else if (metricSelectorResult) {
                metricSelectorResult?.forEach(m => {
                    newSelectedMetrics.push({
                        ...m,
                        Id: m.MetricID,
                        Name: m.MetricName,
                        MetricCustomName: m.MetricCustomName
                    });
                });
                this.settings.selectedMetrics = newSelectedMetrics;
            }
        });
    }

    async removeMetric(metricId: number): Promise<void> {
        const index = this.settings.selectedMetrics.findIndex(m => m.Id === metricId);
        if (index > -1) {
            const updatedMetrics = this.settings.selectedMetrics.filter(m => m.Id!== metricId);
            this.settings.selectedMetrics = updatedMetrics;
            await this.metricSelectorService.updateSelectedMetrics(updatedMetrics);
        }
    }

    handleUseMetricsChange(): void {
        if (!this.settings.useMetrics) {
            this.removeAllMetrics();
        }
    }

    async removeAllMetrics(): Promise<void> {
        this.settings.selectedMetrics = [];
        await this.metricSelectorService.updateSelectedMetrics(this.settings.selectedMetrics);
    }

    getSlideoutContextSectionText(areMetricsEnabled: boolean): string {
        return areMetricsEnabled
            ? this.localizationService.getCachedText("Current_Agent_States_Module", "Metrics_On", "On")
            : this.localizationService.getCachedText("Current_Agent_States_Module", "Metrics_Off", "Off");
    }
    /**
     * This function loops over ctrl.settings.all and builds statesByEState
     */
    categorizeAllStates(allSettings) {
        // Reset previous, if set:
        Object.keys(this.statesByEState).forEach(e => {
            if (Array.isArray(this.statesByEState[e]))
                this.statesByEState[e].splice(0);
        });

        allSettings.forEach(setting => {
            const e = setting.EState;
            // eslint-disable-next-line no-bitwise
            if ((e & EState.Mixed)) { // bitwise AND.  Mixed is only set when multiple other values are set, so simple comparison with == is incorrect
                // Do nothing if the Mixed flag is set.  It shouldn't be set.
                return;
            } else {
                this.statesByEState[e].push(setting);
            }
        });
    }

    validateNameForCleave(state) {
        let split = state.StateDisplayName.split('');
        const blacklist = [' ', '\t', '-', '_', '/', '\\', '&', '(', ')', '.'];
        split = split.filter(c =>  blacklist.indexOf(c) === -1);

        return split.join('');
    };

    getEStateColor(e) {
        switch (e) {
            case EState.Available:
                return this.colors.green;
            case EState.Inbound:
                return this.colors.purple;
            case EState.Outbound:
                return this.colors.yellow;
            default:
                return this.colors.red;
        }
    }

    /**
     * Returns the color for the specified state. The colors for Custom States colors may be changed by the user.
     *
     * @param state State setting object {*}
     */
    getColorForState(state) {
        if (state.EState === EState.Custom) {
            const colorMapKey = state.StateDisplayName.toUpperCase();
            if (this._stateToColorMap && this._stateToColorMap[colorMapKey])
                return this._stateToColorMap[colorMapKey];
            else if (state.IsACW)
                return this.colors.orange;
        }
        return this.getEStateColor(state.EState);
    }

    // Methods
    async loadData() {
        this.isFirstOpen = false;
        if (this.dataLoading) {
            return;
        }
        this.dataLoading = true;
        this._lastLoaded = new Date().getTime();

        this.cachedResponse = await this.service.loadDistinctAgentStates(this.moduleKey); //returns response.data
        this.dataLoading = false;

        this.setupStates(this.cachedResponse);
        this.showSourceRadio = this.cachedResponse.ShowSourceRadio;
    };

    // filtering all settings to those which belong in the left column of the old modal settings ordered by EState with a secondary order of StateDisplayName
    filterLeft(allSettings) {
        this.settings.filteredLeft = [];
        allSettings.forEach(setting => {
            if (this.belongsLeft(setting)) {
                this.settings.filteredLeft.push(setting);
            }
        });

        this.settings.filteredLeft.sort((a,b) => {
            const eStateCompare = a.EState - b.EState;
            if (eStateCompare === 0) {
                return a.StateDisplayName.localeCompare(b.StateDisplayName, undefined, {sensitivity: "base"});
            }
            return eStateCompare;
        });
    }

    // filtering all settings to those which belong in the right column of the old modal settings ordered by EState with a secondary order of StateDisplayName
    filterRight(allSettings) {
        this.settings.filteredRight = [];
        allSettings.forEach(setting => {
            if (this.belongsRight(setting)) {
                this.settings.filteredRight.push(setting);
            }
        });

        this.settings.filteredRight.sort((a,b) => {
            const eStateCompare = a.EState - b.EState;
            if (eStateCompare === 0) {
                return a.StateDisplayName.localeCompare(b.StateDisplayName, undefined, {sensitivity: "base"});
            }
            return eStateCompare;
        });
    }

    // states which belong in the left column
    belongsLeft(state) {
        switch (state.EState) {
            case EState.Available:
            case EState.Inbound:
            case EState.Outbound:
                return true;
            default:
                return false;
        }
    };

    // those that dont belong left belong right
    belongsRight(state) {
        return !this.belongsLeft(state);
    };

    agentStateTypeToEState(agentStateType) {
        switch (agentStateType) {
            case "Inbound":
            case 1:
                return EState.Inbound;
            case "Outbound":
            case 2:
                return EState.Outbound;
            case "Available":
            case 3:
                return EState.Available;
            case "Unavailable":
            case 4:
                return EState.Unavailable;
            default:
                return EState.LoggedOut;
        }
    }

    setupStates(distinctStatesData) {
        const allSettings = [];

        // Check if AgentStateCategories is null, if null return else continue
        if (!distinctStatesData?.AgentStateCategories) {
            return;
        }

        // Loop over the AgentStateCategories:
        Object.keys(distinctStatesData.AgentStateCategories).forEach(agentStateType => {
            const thisEState = this.agentStateTypeToEState(agentStateType);
            const existing = this.statesByEState[thisEState].find(
                state => state.StateDisplayName.localeCompare(
                    distinctStatesData.AgentStateCategories[agentStateType], undefined, { sensitivity: "base" }) === 0);

            if (!existing) {
                const newState = {
                    // These property names must match ModuleResponsiveController.cs -> CurrentAgentStatesModule.ModuleSettings
                    StateDisplayName: distinctStatesData.AgentStateCategories[agentStateType],
                    min: null,
                    max: null,
                    rawMin: "",
                    rawMax: "",
                    Color: "", // Updated separately
                    IsACW: false,
                    enabled: distinctStatesData.DefaultEnabled,
                    EState: thisEState,
                    version: this.version,
                };
                allSettings.push(newState);
                this.statesByEState[thisEState].push(newState);
            }
            else {
                allSettings.push(existing);
            }
        });

        // Loop over the actual DistinctAgentStates:
        distinctStatesData.States.forEach(distinctAgentState => {
            // each object is a ClearView.Quality.AgentStateDetails.DistinctAgentState object
            // Properties: AgentStateName: string, OutStateDescription: string, IsACW: bool,
            const stateDisplayName = distinctAgentState.OutStateDescription || distinctAgentState.AgentStateName;
            if (!stateDisplayName) {
                return; // not adding a state without a name;
            }
            const existing = this.statesByEState[EState.Custom].find(
                state => state.StateDisplayName.localeCompare(stateDisplayName, undefined, { sensitivity: "base" }) === 0);

            if (!existing) {
                const newState = {
                    // These property names must match ModuleResponsiveController.cs -> CurrentAgentStatesModule.ModuleSettings
                    StateDisplayName: stateDisplayName,
                    min: null,
                    max: null,
                    rawMin: "",
                    rawMax: "",
                    Color: "", // Updated separately
                    IsACW: distinctAgentState.IsACW,
                    enabled: distinctStatesData.DefaultEnabled,
                    EState: EState.Custom,
                    version: this.version,
                };
                allSettings.push(newState);
                this.statesByEState[EState.Custom].push(newState);
            }
            else {
                allSettings.push(existing);
            }
        });

        this.filterLeft(allSettings);
        this.filterRight(allSettings);

        this.updateStateColors(allSettings);
        this.updateStateDisplayOrder(allSettings);
        this.settings.all = allSettings;
        this.enabledSettings = this.settings.all.filter(state => state.enabled).length;
    }

    toggleVisible() {
        this.openSettings = !this.openSettings;
    };

    stateChanged() {
        this.enabledSettings = this.settings.all.filter(state => state.enabled).length;
    }

    /**
     * This function is used to transition old settings from previous modules to current.
     *
     * @param  allRestoredStates type {[{}]}
     */
    filterSettings(allRestoredStates) {
        const good = [];
        this.enabledSettings = 0;
        allRestoredStates.forEach(state => {
            if (state.version === this.version && state.EState != null && state.StateDisplayName) {
                // Remove known, unneeded properties:
                delete state.AgentStateName;
                delete state.OutStateDescription;
                delete state.sortRight;
                delete state.CleaveName;
                delete state.DisplayName;
                good.push(state);
                if (state.enabled) {
                    this.enabledSettings ++;
                }
            } // Else, do nothing with this object.  It doesn't get added to the good list.
        });
        return good;
    }

    isEmpty(el) {
        return el.DisplayName.trim() !== '';
    }

    /**
     * This function updates the array statesInDisplayOrder.  The array is used in the template for the Slideout Settings.
     */
    updateStateDisplayOrder(allSettings) {
        this.statesInDisplayOrder.splice(0);

        if (allSettings.length > 0)
            Array.prototype.push.apply(this.statesInDisplayOrder, allSettings);

        this.statesInDisplayOrder.sort((a, b) => {
            const difference = a.EState - b.EState;
            // When the state enum are the same, sort by the StateDisplayName
            if (difference === 0) {
                return a.StateDisplayName.localeCompare(b.StateDisplayName, undefined, { sensitivity: "base" });
            }
            return difference;
        });
    }

    /**
     * For each state in `states`, updates the .Color property.
     *
     * @param states Array of states to update TYPE {[*]}
     */
    updateStateColors(states: StateSetting[]) {
        states.forEach(state => {
            state.Color = this.getColorForState(state);
        });
    }

    /**
     * Checks and returns if the state's EState value is Custom.
     * Used in the template to show the "Edit" text button.
     */
    isStateCustom(state: StateSetting) {
        // eslint-disable-next-line no-bitwise
        return !!state && (state.EState & EState.Custom) !== 0;
    }

    /**
     * Displays the modal allowing the user to pick a custom color for this custom `state`.
     */
    showColorModal(state) {
        if (this._modalOpened || !this.isStateCustom(state))
            return;

        this._modalOpened = true;
        const recentColors = this._stateToColorMap._recentColors || [];
        const modalInstance = this.ngbModalService.open(AgentStatesColorModalComponent, {
            backdrop:'static',
            size:'sm modal-dialog-centered',
        });
        modalInstance.componentInstance.currentColor = state.Color;
        modalInstance.componentInstance.defaultColor = this.getEStateColor(EState.Custom);
        modalInstance.componentInstance.recentColors = recentColors;
        modalInstance.componentInstance.stateDisplayName = state.StateDisplayName;

        // Modal Saved
        modalInstance.result.then(selectedColor => {
            if (!selectedColor) return;
            state.Color = selectedColor;
            selectedColor = selectedColor.toUpperCase();
            // The states returned by the DistinctAgentStates endpoint do not necessarily match the CSMD states, case-wise.
            // Store them in uppercase for lookup.
            const colorMapKey = state.StateDisplayName.toUpperCase();
            if (selectedColor === "DEFAULT") {
                delete this._stateToColorMap[colorMapKey];
            } else {
                this._stateToColorMap[colorMapKey] = selectedColor;
                // If it's not a duplicate, and there are equal to or more than 12 colors (MAX_RECENT_COLORS), delete the excess from the beginning
                if (!recentColors.includes(selectedColor)) {
                    if (recentColors.length >= this.MAX_RECENT_COLORS) {
                        recentColors.splice(0, recentColors.length - this.MAX_RECENT_COLORS + 1);
                    }
                    recentColors.push(selectedColor);
                }
                this._stateToColorMap._recentColors = recentColors; // In case recentColors started off as a new array.
            }

            this.updateStateColors(this.settings.all);
        });

        modalInstance.result.catch(() => { throw Error("Modal failed to open");});
        modalInstance.result.finally(() => {
            this._modalOpened = false;
        });
    }

    public getMetricDisplayName(metric)
    {
        if(metric.MetricCustomName && metric.MetricCustomName != "")
            return metric.MetricCustomName +"*";

        return metric.Abbreviation;
    }
}
